import React, { FunctionComponent, useCallback } from 'react';
import { Box, Link, Typography } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { IImage } from '../../../../common/model/ICarDetails';
import { ICarStockMatch } from '../../../../common/model/ICarStockMatch';
import stockVehicleBlockStyles from './carCardStyles';
import SimpleButton from '../../../../common/components/presentation/simpleButton/SimpleButton';
import CardHeader from './cardHeader/CardHeader';
import CardContent from './cardContent/CardContent';
import Translation from '../../../../common/components/translation/Translation';
import {
  GetCustomizeCar_customizeCar_similarStockCars_prices_totalPrice,
  GetCustomizeCar_customizeCar_similarStockCars_finance,
} from '../../../graphql/queries/__generated__/GetCustomizeCar';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import useWidgetContext from '../../../../common/react/useWidgetContext';
import { delayedScrollToTopOnBack } from '../../../../gqlCommon/service/scrollUtilities';
import useUrlSearchParams from '../../../hooks/useUrlSearchParams';
import useCustomizeCarContext from '../../../hooks/useCustomizeCarContext';
import { StockTypes } from '../../../../common/enum/StockTypes';

export interface ICarCardProps {
  carId: string;
  carImage?: IImage;
  title?: string;
  prices: GetCustomizeCar_customizeCar_similarStockCars_prices_totalPrice[];
  features?: string[];
  onSelectCar: () => void;
  onTransfer?: () => void;
  stockMatch?: ICarStockMatch;
  stockType?: StockTypes;
  carMatchLabel?: string;
  finance: GetCustomizeCar_customizeCar_similarStockCars_finance;
  id?: string;
  minHeight?: string;
  learnMoreLabel?: string;
  learnMoreLink?: string;
  hasDisclaimer?: boolean;
}

const CarCard: FunctionComponent<ICarCardProps> = ({
  carId,
  carImage,
  title = '',
  prices,
  features,
  onSelectCar,
  stockMatch,
  stockType,
  carMatchLabel,
  finance,
  id,
  minHeight = '0',
  learnMoreLabel,
  learnMoreLink: learnMoreUrlFromCarAttributes,
  hasDisclaimer,
}) => {
  const styles = stockVehicleBlockStyles();

  const {
    configuration: { learnMoreUrl: learnMoreUrlFromWidgetConfig },
  } = useWidgetContext();

  const { loadCustomizeCar, selectedCar } = useCustomizeCarContext();

  const history = useHistory();
  const { searchParams } = useUrlSearchParams();
  const carCode = searchParams.carCode;
  const byoCode = selectedCar?.car?.byoCode;
  const isCarSelected = carCode === carId;

  const handleOnSelectCar = useCallback(() => {
    if (!isCarSelected) {
      onSelectCar();
      delayedScrollToTopOnBack(history);
      return;
    }

    loadCustomizeCar({
      carId: byoCode,
    });
  }, [onSelectCar, loadCustomizeCar, byoCode, history, isCarSelected]);

  const learnMoreHref =
    learnMoreUrlFromWidgetConfig && learnMoreLabel
      ? `${learnMoreUrlFromWidgetConfig}${learnMoreLabel}`
      : learnMoreUrlFromCarAttributes && learnMoreLabel
      ? learnMoreUrlFromCarAttributes
      : null;

  const buttonLabel = isCarSelected
    ? 'trans__configurator__stock_section__block_unselect_cta'
    : 'trans__configurator__stock_section__block_select_cta';

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      width={246}
      minHeight={minHeight}
      id={id}
      className={styles.wrapper}
      maxWidth="100%"
      margin="0 auto"
      data-test="stockLite:vehicle:block"
    >
      <CardHeader
        prices={prices}
        carImageSource={carImage?.url}
        onClick={handleOnSelectCar}
        title={title}
        alt={title}
        stockMatch={stockMatch}
        stockType={stockType}
        carMatchLabel={carMatchLabel}
        finance={finance}
        hasDisclaimer={hasDisclaimer}
      />
      <CardContent features={features} hasDisclaimer={hasDisclaimer} />
      <Box className={styles.selectButtonWrapper}>
        <SimpleButton
          buttonClassName={styles.cta}
          type={isCarSelected ? 'simple' : 'filled'}
          dataTest="stockLite:car_card:cta"
          action={handleOnSelectCar}
        >
          <Typography variant="caption">
            <Translation id={buttonLabel} />
          </Typography>
        </SimpleButton>
      </Box>
      {learnMoreHref && (
        <Box width={1} data-test="stockLite:car_card:learn_more_link" className={styles.learnMoreCtaContainer}>
          <Link className={styles.learnMoreCta} href={learnMoreHref} target="_blank" rel="noopner">
            <Typography component="span" className={styles.learnMoreCtaLabel}>
              <Translation id="trans__configurator__stock_section__learn_more_link" />
              <ChevronRightIcon />
            </Typography>
          </Link>
        </Box>
      )}
    </Box>
  );
};

export default CarCard;
